import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { motion, AnimatePresence } from "framer-motion";
import { loadStripe } from "@stripe/stripe-js";
import axios from "axios";

import { AppDispatch, RootState }       from "../../redux/store";
import { addToCart, decrementCartItem, removeFromCart } from "../../redux/cartSlice";
import { listenToClassifiedData }        from "../../redux/classifiedsSlice";
import { nearestDeliveryLocation }       from "../../redux/commercePartnersSlice";
import { calculateClassifiedEarnings }   from "../../redux/classifiedCommerceServiceFeeSlice";
import { electronicsColorAttributes, electronicsSizeAttributes } from "../ATTRIBUTES/ElectronicsAttributes";

import {
  ArrowLeft, ShoppingBag, Trash2, ShieldCheck, Heart,
  CreditCard, RotateCcw, Package, MapPin, Truck,
  Lock, Plus, Minus,
  AlertCircle, X, Check, Gift,
} from "lucide-react";

/* ════════════════════════════════════════════════════════════
   DESIGN TOKENS — CosMeta Blue/Gold (mirrors Glam B&W palette)
════════════════════════════════════════════════════════════ */
const T = {
  bg:         "#FFFFFF",
  bgSoft:     "#F7F9FF",
  bgMuted:    "#EEF4FF",
  black:      "#060D1F",
  blackMid:   "#0A1628",
  blackSoft:  "#1A2840",
  border:     "#DCE8FF",
  borderDark: "#A8C4D8",
  muted:      "#6B8CB8",
  mutedSoft:  "#A8C4D8",
  blue:       "#0A66C2",
  blueLight:  "#5BCBFF",
  gold:       "#FEBA02",
  goldLight:  "#FFF8DC",
  red:        "#CC0000",
  grad:       "linear-gradient(135deg,#003580 0%,#0061fe 60%,#FEBA02 100%)",
};

const CHECKOUT_URL =
  process.env.REACT_APP_META_CHECKOUT_URL ??
  "https://europe-west3-cosmetaa.cloudfunctions.net/createElectronicsCheckoutSession";

type DeliveryMeta = {
  deliveryFee:  number;
  deliveryTime: number;
  fulfilledBy:  { isFBA: boolean; isFBS: boolean };
};

type StripeSessionResponse = { id: string };

/* ════════════════════════════════════════════════════════════
   HELPERS
════════════════════════════════════════════════════════════ */
const fmtPrice = (n: number, sym = "$") =>
  `${sym}${Number(n ?? 0).toLocaleString("en-US", { minimumFractionDigits: 2 })}`;

const getSizeName  = (id?: number) =>
  id ? electronicsSizeAttributes.values.find((s: any) => s.id === id)?.name ?? null : null;
const getColorName = (id?: number) =>
  id ? electronicsColorAttributes.values.find((c: any) => c.id === id)?.name ?? null : null;
const getColorHex  = (id?: number) =>
  id ? electronicsColorAttributes.values.find((c: any) => c.id === id)?.hexCodes?.[0] ?? null : null;

/* ════════════════════════════════════════════════════════════
   FEE ROW  (exact Glam pattern)
════════════════════════════════════════════════════════════ */
const FeeRow: React.FC<{
  label: string; value: string;
  sub?: string; bold?: boolean; muted?: boolean;
  green?: boolean; red?: boolean; loading?: boolean;
}> = ({ label, value, sub, bold, muted, green, red, loading }) => (
  <div style={{
    display: "flex", justifyContent: "space-between", alignItems: "center",
    padding: "10px 0", borderBottom: `1px solid ${T.border}`,
  }}>
    <div>
      <div style={{ fontSize: bold ? 14 : 12, fontWeight: bold ? 700 : 500, color: muted ? T.muted : T.black, fontFamily: "'DM Sans',sans-serif", letterSpacing: bold ? "0.02em" : "0.04em" }}>
        {label}
      </div>
      {sub && <div style={{ fontSize: 10, color: T.muted, fontFamily: "'DM Sans',sans-serif", marginTop: 2 }}>{sub}</div>}
    </div>
    {loading ? (
      <span style={{ fontSize: 11, color: T.muted, fontFamily: "'DM Sans',sans-serif" }}>Calculating…</span>
    ) : (
      <span style={{ fontSize: bold ? 22 : 13, fontWeight: bold ? 700 : 600, color: red ? T.red : green ? "#16a34a" : T.black, fontFamily: bold ? "'Playfair Display',serif" : "'DM Sans',sans-serif", letterSpacing: bold ? "0.01em" : 0 }}>
        {value}
      </span>
    )}
  </div>
);

/* ════════════════════════════════════════════════════════════
   CART ITEM CARD  (exact Glam CartItemCard pattern)
════════════════════════════════════════════════════════════ */
const CartItemCard: React.FC<{
  item:            any;
  listing:         any;
  deliveryFee:     number;
  deliveryLoading: boolean;
  index:           number;
  sym:             string;
  onInc:           () => void;
  onDec:           () => void;
  onRemove:        () => void;
  onNavigate:      () => void;
}> = ({ item, listing, deliveryFee, deliveryLoading, index, sym, onInc, onDec, onRemove, onNavigate }) => {
  const [removing, setRemoving] = useState(false);
  const [wished,   setWished]   = useState(false);

  const colorName = getColorName(item.colorId);
  const colorHex  = getColorHex(item.colorId);
  const sizeName  = getSizeName(item.sizeId);

  const previewImg =
    item.colorId && listing?.variants?.[item.colorId]?.imageUrl
      ? listing.variants[item.colorId].imageUrl
      : listing?.photos?.[0] ?? item.photo;

  const handleRemove = () => { setRemoving(true); setTimeout(onRemove, 300); };

  return (
    <motion.div
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: removing ? 0 : 1, y: removing ? -8 : 0, scale: removing ? 0.97 : 1 }}
      transition={{ duration: 0.3, delay: removing ? 0 : index * 0.06 }}
      style={{ border: `1px solid ${T.border}`, background: T.bg, marginBottom: 12, overflow: "hidden", transition: "box-shadow .2s" }}
      onMouseEnter={e => (e.currentTarget as HTMLElement).style.boxShadow = "0 6px 28px rgba(10,102,194,.10)"}
      onMouseLeave={e => (e.currentTarget as HTMLElement).style.boxShadow = "none"}
    >
      <div style={{ display: "flex", gap: 0 }}>
        {/* Image */}
        <div onClick={onNavigate}
          style={{ width: 96, flexShrink: 0, background: T.bgMuted, cursor: "pointer", position: "relative", overflow: "hidden" }}>
          {previewImg ? (
            <img src={previewImg} alt={item.name}
              style={{ width: "100%", height: "100%", objectFit: "cover", minHeight: 120, transition: "transform .4s" }}
              onMouseEnter={e => (e.currentTarget.style.transform = "scale(1.05)")}
              onMouseLeave={e => (e.currentTarget.style.transform = "scale(1)")} />
          ) : (
            <div style={{ width: "100%", minHeight: 120, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Package size={24} color={T.mutedSoft} />
            </div>
          )}
        </div>

        {/* Info */}
        <div style={{ flex: 1, padding: "14px 16px", display: "flex", flexDirection: "column", gap: 7 }}>
          {/* Name */}
          <div style={{ fontSize: 14, fontFamily: "'Playfair Display',serif", fontWeight: 400, color: T.black, lineHeight: 1.3, cursor: "pointer" }} onClick={onNavigate}>
            {item.name}
          </div>

          {/* Colour + Size chip */}
          {(colorName || sizeName) && (
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              {colorHex && <div style={{ width: 12, height: 12, borderRadius: "50%", background: colorHex, border: `1px solid ${T.border}`, flexShrink: 0 }} />}
              <span style={{ fontSize: 10, color: T.muted, fontFamily: "'DM Sans',sans-serif", letterSpacing: "0.08em" }}>
                {[colorName, sizeName].filter(Boolean).join(" · ")}
              </span>
            </div>
          )}

          {/* Delivery badge */}
          {(deliveryFee > 0 || deliveryLoading) && (
            <div style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "3px 8px", background: T.bgMuted, border: `1px solid ${T.border}`, alignSelf: "flex-start" }}>
              <Truck size={10} color={T.muted} />
              <span style={{ fontSize: 9, fontWeight: 700, color: T.muted, fontFamily: "'DM Sans',sans-serif", letterSpacing: "0.1em" }}>
                {deliveryLoading ? "CALCULATING…" : deliveryFee === 0 ? "FREE DELIVERY" : `${fmtPrice(deliveryFee, "€")} DELIVERY`}
              </span>
            </div>
          )}

          {/* Bottom row: qty stepper + price + actions */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 4 }}>
            {/* Qty stepper */}
            <div style={{ display: "flex", alignItems: "center", gap: 0, border: `1px solid ${T.border}` }}>
              <button onClick={onDec}
                style={{ width: 30, height: 30, border: "none", background: T.bg, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", borderRight: `1px solid ${T.border}` }}>
                <Minus size={11} color={T.black} />
              </button>
              <div style={{ width: 34, height: 30, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 700, color: T.black, fontFamily: "'DM Sans',sans-serif" }}>
                {item.qty}
              </div>
              <button onClick={onInc}
                style={{ width: 30, height: 30, border: "none", background: T.bg, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", borderLeft: `1px solid ${T.border}` }}>
                <Plus size={11} color={T.black} />
              </button>
            </div>

            {/* Price + wishlist + remove */}
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{ textAlign: "right" }}>
                <div style={{ fontSize: 16, fontFamily: "'Playfair Display',serif", fontWeight: 500, color: T.black }}>
                  {fmtPrice(item.qty * item.price, sym)}
                </div>
                <div style={{ fontSize: 10, color: T.muted, fontFamily: "'DM Sans',sans-serif" }}>
                  {fmtPrice(item.price, sym)} each
                </div>
              </div>

              <button onClick={() => setWished(v => !v)}
                style={{ width: 30, height: 30, border: `1px solid ${T.border}`, background: T.bg, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Heart size={12} color={wished ? T.red : T.muted} fill={wished ? T.red : "none"} />
              </button>

              <button onClick={handleRemove}
                style={{ width: 30, height: 30, border: `1px solid ${T.border}`, background: T.bg, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", transition: "all .15s" }}
                onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.borderColor = T.red; (e.currentTarget as HTMLButtonElement).style.background = "#FFF5F5"; }}
                onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.borderColor = T.border; (e.currentTarget as HTMLButtonElement).style.background = T.bg; }}>
                <Trash2 size={12} color={T.red} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

/* ════════════════════════════════════════════════════════════
   MAIN COMPONENT
════════════════════════════════════════════════════════════ */
const MetaCommerceCheckout: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const navigate   = useNavigate();
  const dispatch   = useDispatch<AppDispatch>();

  const items    = useSelector((s: RootState) => s.cart.items);
  const listings = useSelector((s: RootState) => s.classifieds.listings);
  const fees     = useSelector((s: RootState) => s.classifiedCommerceServiceFees);

  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  useEffect(() => {
    const fn = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", fn); return () => window.removeEventListener("resize", fn);
  }, []);

  /* ── Geolocation ── */
  const [userLoc, setUserLoc] = useState<{ latitude: number; longitude: number } | null>(null);
  useEffect(() => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      pos => setUserLoc({ latitude: pos.coords.latitude, longitude: pos.coords.longitude }),
      () => {}
    );
  }, []);

  /* ── Delivery ── */
  const [deliveryByBrand,  setDeliveryByBrand]  = useState<Record<string, number>>({});
  const [deliveryMeta,     setDeliveryMeta]      = useState<Record<string, DeliveryMeta>>({});
  const [deliveryTotal,    setDeliveryTotal]     = useState(0);
  const [deliveryLoading,  setDeliveryLoading]   = useState(false);

  useEffect(() => {
    if (!items.length || !userLoc) return;
    const calc = async () => {
      setDeliveryLoading(true);
      try {
        const brandIds = Array.from(new Set(items.map((i: any) => i.brandId).filter(Boolean)));
        let total = 0;
        const perBrand: Record<string, number> = {};
        const meta: Record<string, DeliveryMeta> = {};
        for (const bId of brandIds) {
          try {
            const res = await dispatch(nearestDeliveryLocation({
              brandId: bId as string,
              userLatitude: userLoc.latitude,
              userLongitude: userLoc.longitude,
            })).unwrap();
            if (!res.matchedZone) continue;
            perBrand[bId as string] = res.deliveryFee;
            total += res.deliveryFee;
            meta[bId as string] = {
              deliveryFee:  res.deliveryFee,
              deliveryTime: res.matchedZone.deliveryTime ?? 1,
              fulfilledBy:  res.matchedZone.fulfilledBy  ?? { isFBA: false, isFBS: true },
            };
          } catch {}
        }
        setDeliveryByBrand(perBrand);
        setDeliveryTotal(total);
        setDeliveryMeta(meta);
      } catch {}
      setDeliveryLoading(false);
    };
    calc();
  }, [items, userLoc, dispatch]);

  /* ── Load listing data ── */
  useEffect(() => {
    const ids = Array.from(new Set(items.map((i: any) => i.listingId)));
    ids.forEach((id: any) => dispatch(listenToClassifiedData(id)));
  }, [items, dispatch]);

  /* ── Fees ── */
  useEffect(() => {
    if (!items.length) return;
    dispatch(calculateClassifiedEarnings({ items, deliveryByBrand }));
  }, [items, deliveryByBrand, dispatch]);

  /* ── Redirect if empty ── */
  useEffect(() => {
    if (!items.length) navigate("/MetaCommerce");
  }, [items, navigate]);

  const getListing = (id: string) => listings.find((l: any) => l.listingId === id);

  const { subtotal, totalQty, guestsFee, hostProfits, platformProfit, totalCharged } = fees;
  const grandTotal = totalCharged;

  /* ── Stripe ── */
  const [paying,   setPaying]   = useState(false);
  const [payError, setPayError] = useState<string | null>(null);

  const canPay = !paying && !deliveryLoading && items.length > 0 && grandTotal > 0;

  const handlePay = async () => {
    if (!canPay) return;
    setPaying(true); setPayError(null);
    try {
      const sanitizedItems = items.map(({ photo, ...rest }: any) => ({
        ...rest,
        deliveryTime:   deliveryMeta[rest.brandId]?.deliveryTime ?? 1,
        fulfilledBy:    deliveryMeta[rest.brandId]?.fulfilledBy  ?? { isFBA: false, isFBS: true },
        deliveryStatus: { sellerDelivered: false, buyerReceived: false },
      }));
      const res = await axios.post<StripeSessionResponse>(CHECKOUT_URL, {
        userId, items: sanitizedItems,
        totalQty, subtotal, guestsFee, hostProfits, platformProfit, totalCharged: grandTotal,
      });
      const stripe = await loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY!);
      if (!stripe) throw new Error("Stripe failed to load");
      const { error } = await (stripe as any).redirectToCheckout({ sessionId: res.data.id });
      if (error) throw new Error(error.message);
    } catch (e: any) {
      setPayError(e?.message ?? "Payment failed. Please try again.");
      setPaying(false);
    }
  };

  /* ════════════════════════════════════════════════════════
     RENDER
  ════════════════════════════════════════════════════════ */
  return (
    <div style={{ minHeight: "100vh", background: T.bgSoft, fontFamily: "'DM Sans',sans-serif" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@300;400;500;600&family=DM+Sans:wght@300;400;500;600;700;800&display=swap');
        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
        button { font-family: inherit; cursor: pointer; }
        @keyframes metaSpin { to { transform: rotate(360deg); } }
      `}</style>

      {/* ══ HEADER ══ */}
      <header style={{ position: "sticky", top: 0, zIndex: 300, background: T.black, borderBottom: "1px solid #0A1628" }}>
        {/* Announcement bar */}
        <div style={{ background: T.grad, textAlign: "center", fontSize: 10, padding: "6px 0", letterSpacing: "0.18em", fontWeight: 600, color: "#fff" }}>
          SECURE CHECKOUT — SSL ENCRYPTED · POWERED BY STRIPE
        </div>

        <div style={{ display: "flex", alignItems: "center", padding: "0 20px", height: 56, gap: 14 }}>
          <button onClick={() => navigate(-1)}
            style={{ background: "none", border: "none", color: "rgba(255,255,255,.6)", display: "flex", alignItems: "center", gap: 5, fontSize: 11, letterSpacing: "0.06em" }}>
            <ArrowLeft size={15} /> Back
          </button>

          {/* Logo centred */}
          <div onClick={() => navigate(`/MetaCommerce/${userId}`)}
            style={{ position: "absolute", left: "50%", transform: "translateX(-50%)", cursor: "pointer" }}>
            <span style={{ fontFamily: "'Playfair Display',serif", fontSize: 20, fontWeight: 400, color: T.bg, letterSpacing: "0.2em" }}>
              COSMETA
            </span>
          </div>

          <div style={{ flex: 1 }} />

          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <ShoppingBag size={17} color="rgba(255,255,255,.7)" />
            <span style={{ fontSize: 12, color: "rgba(255,255,255,.7)", fontWeight: 700 }}>{totalQty}</span>
          </div>
          <Lock size={14} color={T.gold} />
        </div>
      </header>

      {/* ══ BODY ══ */}
      <div style={{
        maxWidth: 1100, margin: "0 auto",
        padding: isMobile ? "20px 16px 100px" : "36px 40px 60px",
        display: "grid",
        gridTemplateColumns: isMobile ? "1fr" : "1fr 380px",
        gap: 32, alignItems: "start",
      }}>

        {/* ── LEFT: Cart items ── */}
        <div>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 18 }}>
            <h2 style={{ fontFamily: "'Playfair Display',serif", fontSize: 22, fontWeight: 300, color: T.black, letterSpacing: "0.04em" }}>
              Your Order
            </h2>
            <span style={{ fontSize: 10, color: T.muted, letterSpacing: "0.15em", fontFamily: "'DM Sans',sans-serif" }}>
              {totalQty} ITEM{totalQty !== 1 ? "S" : ""}
            </span>
          </div>

          {items.map((item: any, i: number) => (
            <CartItemCard
              key={`${item.listingId}-${item.colorId ?? "nc"}-${item.sizeId ?? "ns"}`}
              item={item}
              listing={getListing(item.listingId)}
              index={i}
              deliveryFee={deliveryByBrand[item.brandId] ?? 0}
              deliveryLoading={deliveryLoading}
              sym="€"
              onInc={() => dispatch(addToCart({ listingId: item.listingId, name: item.name, hostId: item.hostId, price: item.price, colorId: item.colorId, sizeId: item.sizeId, qty: 1, brandId: item.brandId }))}
              onDec={() => dispatch(decrementCartItem({ listingId: item.listingId, colorId: item.colorId, sizeId: item.sizeId }))}
              onRemove={() => dispatch(removeFromCart({ listingId: item.listingId, colorId: item.colorId, sizeId: item.sizeId }))}
              onNavigate={() => navigate(`/metacommercedetails/${item.listingId}`)}
            />
          ))}

          {/* Delivery location strip */}
          <AnimatePresence>
            {userLoc && !deliveryLoading && (
              <motion.div
                initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                style={{ display: "flex", alignItems: "center", gap: 10, background: T.bg, border: `1px solid ${T.border}`, padding: "12px 16px", marginTop: 8 }}>
                <MapPin size={14} color={T.muted} />
                <span style={{ fontSize: 11, color: T.black, fontFamily: "'DM Sans',sans-serif" }}>
                  {deliveryTotal === 0 ? "Free delivery to your location" : `Delivery: €${deliveryTotal.toFixed(2)} to your location`}
                </span>
                {deliveryTotal === 0 && <Check size={12} color={T.gold} />}
              </motion.div>
            )}
          </AnimatePresence>

          {/* Trust strip — mobile only */}
          {isMobile && (
            <div style={{ display: "flex", justifyContent: "space-around", padding: "20px 0", borderTop: `1px solid ${T.border}`, marginTop: 16 }}>
              {[
                { icon: <ShieldCheck size={16} color={T.muted} />, label: "Secure Payment" },
                { icon: <RotateCcw   size={16} color={T.muted} />, label: "30-Day Returns" },
                { icon: <Truck       size={16} color={T.muted} />, label: "Fast Delivery"  },
              ].map(b => (
                <div key={b.label} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 5 }}>
                  {b.icon}
                  <span style={{ fontSize: 9, fontWeight: 700, color: T.muted, fontFamily: "'DM Sans',sans-serif", letterSpacing: "0.1em" }}>
                    {b.label.toUpperCase()}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* ── RIGHT: Summary + Pay ── */}
        <div style={{ position: isMobile ? "static" : "sticky", top: 96 }}>

          {/* Summary card */}
          <div style={{ background: T.bg, border: `1px solid ${T.border}`, padding: "24px 22px", marginBottom: 14 }}>
            <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: "0.22em", color: T.muted, fontFamily: "'DM Sans',sans-serif", marginBottom: 16 }}>
              ORDER SUMMARY
            </div>

            <FeeRow
              label="Subtotal"
              value={`€${subtotal.toFixed(2)}`}
              sub={`${totalQty} item${totalQty !== 1 ? "s" : ""}`}
            />
            <FeeRow
              label="Service Fee"
              value={`€${guestsFee.toFixed(2)}`}
              sub="Platform fee"
              muted
            />
            <FeeRow
              label="Delivery"
              value={deliveryTotal === 0 ? "FREE" : `€${deliveryTotal.toFixed(2)}`}
              green={!deliveryLoading && deliveryTotal === 0}
              loading={deliveryLoading}
            />

            {/* Total */}
            <div style={{ paddingTop: 14, display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
              <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.15em", color: T.black, fontFamily: "'DM Sans',sans-serif" }}>TOTAL</span>
              <span style={{ fontSize: 28, fontWeight: 300, color: T.black, fontFamily: "'Playfair Display',serif", letterSpacing: "0.01em" }}>
                €{grandTotal.toFixed(2)}
              </span>
            </div>
            <div style={{ fontSize: 10, color: T.muted, fontFamily: "'DM Sans',sans-serif", textAlign: "right", marginTop: 4 }}>
              All taxes included · Charged in EUR
            </div>
          </div>

          {/* Pay button */}
          <motion.button
            whileHover={canPay ? { scale: 1.01 } as any : {}}
            whileTap={canPay ? { scale: 0.98 } as any : {}}
            onClick={handlePay}
            disabled={!canPay}
            style={{
              width: "100%", padding: "16px 0",
              background: canPay ? T.grad : T.bgMuted,
              color: canPay ? "#fff" : T.muted,
              border: "none",
              fontSize: 11, fontWeight: 700, letterSpacing: "0.22em",
              fontFamily: "'DM Sans',sans-serif",
              cursor: canPay ? "pointer" : "not-allowed",
              display: "flex", alignItems: "center", justifyContent: "center", gap: 10,
              transition: "all .2s", marginBottom: 10,
              boxShadow: canPay ? "0 12px 40px rgba(10,102,194,.35)" : "none",
            }}>
            {paying ? (
              <span style={{ width: 14, height: 14, border: "2px solid rgba(255,255,255,.6)", borderTopColor: "transparent", borderRadius: "50%", animation: "metaSpin 1s linear infinite" }} />
            ) : (
              <Lock size={13} />
            )}
            {paying
              ? "PROCESSING…"
              : deliveryLoading
              ? "CALCULATING DELIVERY…"
              : `PAY €${grandTotal.toFixed(2)}`}
          </motion.button>

          {/* Error */}
          <AnimatePresence>
            {payError && (
              <motion.div
                initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                style={{ display: "flex", alignItems: "flex-start", gap: 8, background: "#FFF5F5", border: "1px solid #FFC8C8", padding: "12px 14px", marginBottom: 10 }}>
                <AlertCircle size={13} color={T.red} style={{ flexShrink: 0, marginTop: 1 }} />
                <span style={{ fontSize: 11, color: T.red, fontFamily: "'DM Sans',sans-serif", flex: 1 }}>{payError}</span>
                <button onClick={() => setPayError(null)} style={{ background: "none", border: "none", padding: 0 }}>
                  <X size={12} color={T.red} />
                </button>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Stripe badge */}
          <div style={{ textAlign: "center", marginBottom: 16 }}>
            <span style={{ fontSize: 10, color: T.muted, fontFamily: "'DM Sans',sans-serif", letterSpacing: "0.1em" }}>
              PAYMENTS SECURED BY STRIPE
            </span>
          </div>

          {/* Trust badges — 3-col grid */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginBottom: 16 }}>
            {[
              { icon: <ShieldCheck size={14} color={T.muted} />, label: "Encrypted" },
              { icon: <CreditCard  size={14} color={T.muted} />, label: "Stripe"    },
              { icon: <RotateCcw   size={14} color={T.muted} />, label: "Refunds"   },
            ].map(b => (
              <div key={b.label} style={{ border: `1px solid ${T.border}`, padding: "10px 8px", display: "flex", flexDirection: "column", alignItems: "center", gap: 5, background: T.bg }}>
                {b.icon}
                <span style={{ fontSize: 9, fontWeight: 700, color: T.muted, fontFamily: "'DM Sans',sans-serif", letterSpacing: "0.1em" }}>
                  {b.label.toUpperCase()}
                </span>
              </div>
            ))}
          </div>

          {/* Delivery estimate */}
          {Object.keys(deliveryMeta).length > 0 && (
            <div style={{ border: `1px solid ${T.border}`, background: T.bg, padding: "14px 16px", marginBottom: 14 }}>
              <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: "0.18em", color: T.muted, fontFamily: "'DM Sans',sans-serif", marginBottom: 10 }}>
                ESTIMATED DELIVERY
              </div>
              {Object.entries(deliveryMeta).map(([bId, meta]) => (
                <div key={bId} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                  <Truck size={12} color={T.muted} />
                  <span style={{ fontSize: 11, color: T.black, fontFamily: "'DM Sans',sans-serif" }}>
                    {meta.fulfilledBy.isFBA ? "Platform fulfilled" : "Seller fulfilled"} ·{" "}
                    <strong>{meta.deliveryTime} day{meta.deliveryTime !== 1 ? "s" : ""}</strong>
                  </span>
                </div>
              ))}
            </div>
          )}

          {/* Benefits */}
          <div>
            {[
              { icon: <Gift        size={13} color={T.gold}  />, text: "Complimentary gift wrapping available" },
              { icon: <ShieldCheck size={13} color={T.muted} />, text: "Every product verified by CosMeta" },
              { icon: <RotateCcw   size={13} color={T.muted} />, text: "Free returns within 30 days of delivery" },
            ].map((b, i) => (
              <div key={i} style={{ display: "flex", gap: 8, alignItems: "flex-start", padding: "8px 0", borderBottom: i < 2 ? `1px solid ${T.border}` : "none" }}>
                <span style={{ marginTop: 1, flexShrink: 0 }}>{b.icon}</span>
                <span style={{ fontSize: 11, color: T.muted, fontFamily: "'DM Sans',sans-serif", lineHeight: 1.6 }}>{b.text}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Mobile sticky CTA ── */}
      {isMobile && (
        <div style={{ position: "fixed", bottom: 0, left: 0, right: 0, background: T.bg, borderTop: `1px solid ${T.border}`, padding: "12px 16px", zIndex: 200 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
            <span style={{ fontSize: 11, color: T.muted, fontFamily: "'DM Sans',sans-serif" }}>Total</span>
            <span style={{ fontSize: 20, fontFamily: "'Playfair Display',serif", fontWeight: 400, color: T.black }}>
              €{grandTotal.toFixed(2)}
            </span>
          </div>
          <motion.button
            whileTap={canPay ? { scale: 0.98 } as any : {}}
            onClick={handlePay}
            disabled={!canPay}
            style={{
              width: "100%", padding: "14px 0",
              background: canPay ? T.grad : T.bgMuted,
              color: canPay ? "#fff" : T.muted,
              border: "none",
              fontSize: 11, fontWeight: 700, letterSpacing: "0.2em",
              fontFamily: "'DM Sans',sans-serif",
              cursor: canPay ? "pointer" : "not-allowed",
              display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
              boxShadow: canPay ? "0 8px 28px rgba(10,102,194,.35)" : "none",
            }}>
            {paying
              ? <span style={{ width: 14, height: 14, border: "2px solid rgba(255,255,255,.6)", borderTopColor: "transparent", borderRadius: "50%", animation: "metaSpin 1s linear infinite" }} />
              : <Lock size={13} />}
            {paying ? "PROCESSING…" : `PAY €${grandTotal.toFixed(2)}`}
          </motion.button>
        </div>
      )}

      {/* Footer watermark */}
      <div style={{ textAlign: "center", padding: "32px 0 20px", borderTop: `1px solid ${T.border}`, marginTop: 20 }}>
        <div style={{ fontFamily: "'Playfair Display',serif", fontSize: "min(14vw, 120px)", fontWeight: 300, color: T.black, letterSpacing: "0.15em", opacity: 0.04, lineHeight: 1, userSelect: "none" }}>
          COSMETA
        </div>
      </div>
    </div>
  );
};

export default MetaCommerceCheckout;